import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Settings, Info } from 'lucide-react';
import PensionSetupInfo from './PensionSetupInfo';
import { BankAccount, PensionConsent } from '@/types/pensions';

interface PensionSettingsProps {
  bankAccount: BankAccount | null;
  consent: PensionConsent | null;
  isLoading: boolean;
  onBankAccountUpdate: (bankAccount: BankAccount) => void;
  onConsentUpdate: (consent: PensionConsent) => void;
  showPostponementMode?: boolean;
}

export default function PensionSettings({
  bankAccount,
  consent,
  isLoading,
  onBankAccountUpdate,
  onConsentUpdate,
  showPostponementMode = false,
}: PensionSettingsProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2 mb-4">
          <Settings className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold">Pension Settings</h2>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings Header */}
      <div className="flex items-center space-x-2 mb-4">
        <Settings className="h-5 w-5 text-gray-600" />
        <h2 className="text-lg font-semibold">Pension Settings</h2>
      </div>

      {/* Settings Description */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Configure your pension setup requirements including bank account details and consent preferences. 
          These settings apply to all pension enrollments and contributions.
        </AlertDescription>
      </Alert>

      {/* Pension Setup Configuration */}
      <PensionSetupInfo
        bankAccount={bankAccount}
        consent={consent}
        isLoading={false}
        onBankAccountUpdate={onBankAccountUpdate}
        onConsentUpdate={onConsentUpdate}
        showPostponementMode={showPostponementMode}
      />

      {/* Future Settings Sections */}
      {/* This is where additional settings can be added in the future */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Additional Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600 space-y-2">
            <p>Future pension configuration options will appear here, such as:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Auto-enrollment preferences</li>
              <li>Notification settings</li>
              <li>Default contribution rates</li>
              <li>Reporting preferences</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
